import { useState, useRef, useEffect, useCallback } from 'react';

export const useSpeechToText = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState(null);

  const audioStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const workletNodeRef = useRef(null);
  const onStopRef = useRef(null);
  const socketRef = useRef(null);
  const recordingContextRef = useRef({
    sessionId: null,
    languageCode: null,
    sampleRate: 16000,
    isActive: false,
  });

  const stopRecording = useCallback((socket, options = {}) => {
    const { onStop, sessionId, languageCode, force = false, skipSocket = false } = options || {};
    const stopCallback = typeof onStop === 'function' ? onStop : onStopRef.current;

    const state = recordingContextRef.current;
    const wasActive = !!state.isActive;
    if (!force && !wasActive) {
      return;
    }

    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }

    const resolvedSessionId = sessionId ?? state.sessionId;
    const resolvedLanguageCode = languageCode ?? state.languageCode;
    const socketToUse = socket || socketRef.current;

    if (!skipSocket && socketToUse && socketToUse.readyState === WebSocket.OPEN && resolvedSessionId) {
      socketToUse.send(
        JSON.stringify({
          type: 'stop_audio',
          session_id: resolvedSessionId,
          language_code: resolvedLanguageCode || undefined,
        }),
      );
    }

    state.isActive = false;

    setIsRecording(currentIsRecording => {
      if (currentIsRecording) {
        setIsSpeaking(false);
        if (stopCallback) {
          setTimeout(stopCallback, 100);
        }
      } else if (force && stopCallback) {
        // Ensure stop callback still fires when forcing a secondary stop.
        setTimeout(stopCallback, 0);
      }
      return false; // Always return false to ensure recording is stopped
    });
  }, []);

  const startRecording = async (socket, options = {}) => {
    const {
      sessionId,
      languageCode,
      sampleRate = 16000,
      onStop,
    } = options || {};

    setError(null);
    if (isRecording) {
      return;
    }

    if (!socket || socket.readyState !== WebSocket.OPEN) {
      setError("WebSocket connection is not available.");
      return;
    }

    if (!sessionId) {
      setError("Unable to start recording without an active session.");
      return;
    }
    
    onStopRef.current = onStop;
    socketRef.current = socket;
    recordingContextRef.current = {
      sessionId,
      languageCode: languageCode || null,
      sampleRate,
      isActive: true,
    };

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      setIsRecording(true);

      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate,
      });
      
      await audioContextRef.current.audioWorklet.addModule('/audio-processor.js?v=2');
      workletNodeRef.current = new AudioWorkletNode(audioContextRef.current, 'audio-processor');
      
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(workletNodeRef.current);
      workletNodeRef.current.connect(audioContextRef.current.destination);

      workletNodeRef.current.port.onmessage = (event) => {
        // Server-side VAD handles silence detection — no client-side silence check needed.
        const pcmData = event.data;
        if (socket && socket.readyState === WebSocket.OPEN && pcmData instanceof Int16Array) {
          const base64String = btoa(
            new Uint8Array(pcmData.buffer)
              .reduce((data, byte) => data + String.fromCharCode(byte), '')
          );
          const message = {
            type: 'audio_chunk',
            session_id: sessionId,
            audio_base64: base64String,
            sample_rate: sampleRate,
            language_code: languageCode || undefined,
            content_type: 'audio/pcm',
          };
          socket.send(JSON.stringify(message));
        }
      };

    } catch (err) {
      console.error('Error accessing microphone or setting up audio worklet:', err);
      setError('Microphone access denied or audio setup failed.');
      setIsRecording(false);
      recordingContextRef.current.isActive = false;
    }
  };

  useEffect(() => {
    return () => {
      if (isRecording) {
        stopRecording(undefined, { force: true, skipSocket: true });
      }
    };
  }, [isRecording, stopRecording]);

  return {
    isRecording,
    isSpeaking,
    error,
    startRecording,
    stopRecording,
    setIsRecording, // Expose setter for external control
    setIsSpeaking,
    audioContext: audioContextRef.current,
    mediaStream: audioStreamRef.current,
  };
};
